import React, { useState } from "react";
import Topbar from "@/components/Topbar";
import PresenceVessel from "@/companion/PresenceVessel";
import CompanionPresence from "@/companion/CompanionPresence";
import { getCompanionProfileById } from "@/companion/resolver";
import { groupBoutiquesByMarket, rainbowMeta } from "@/lib/api";

/**
 * Presence Lab — SONMI's canonical reference surface for the PresenceVessel.
 *
 * Not a Toto page. This is the platform's material reference. Every future
 * companion is reviewed against this surface before shipping. The vessel is
 * shown identically across sizes, ambient tints, and presence states.
 *
 * Sizes:      56px (sidebar) · 120px (header) · 256px (marketing hero)
 * Ambients:   Every boutique from the shared registry + Rainbow (pearl)
 * States:     present · supporting · quiet · dormant
 * Occupants:  Toto (Rainbow's westie) · Morrow (empty vessel · reference)
 */

const SIZES = [
  { label: "Sidebar", size: 56 },
  { label: "Header", size: 120 },
  { label: "Marketing hero", size: 256 },
];

// The ambient matrix is generated from the shared boutique registry so a
// new boutique (RNO, CC, or any future one) appears in the lab
// automatically without touching this file.
const AMBIENTS = [
  ...groupBoutiquesByMarket().flatMap(({ market, boutiques }) =>
    boutiques.map((b) => ({ key: b.presenceTint, label: `${b.code} · ${b.shortName}`, market }))
  ),
  { key: rainbowMeta.presenceTint, label: "Rainbow (pearl)", market: rainbowMeta.market },
];

const STATES = ["present", "supporting", "quiet", "dormant"];

export default function PresenceLab() {
  const [occupantId, setOccupantId] = useState("toto");
  const [ambient, setAmbient] = useState("rainbow");
  const activeProfile = getCompanionProfileById(occupantId);

  return (
    <div className="min-h-[calc(100vh-56px)] bg-stone-950 text-stone-100">
      <Topbar title="Presence Lab" />
      <div className="mx-auto max-w-6xl px-8 py-10">
        <header className="mb-10">
          <h1 className="font-serif-display text-4xl text-stone-100">
            Presence Vessel
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-stone-400 leading-relaxed">
            SONMI's universal presence surface. Completely transparent. Never branded.
            Companions inhabit it. The vessel communicates presence — not identity.
            This surface is the canonical reference; every future companion is reviewed here.
          </p>
        </header>

        {/* Controls */}
        <section className="mb-10 flex flex-wrap gap-6" data-testid="pl-controls">
          <ControlGroup label="Occupant">
            {["toto", "morrow"].map((id) => (
              <Chip
                key={id}
                active={occupantId === id}
                onClick={() => setOccupantId(id)}
                testid={`pl-occupant-${id}`}
              >
                {getCompanionProfileById(id).displayName}
                {getCompanionProfileById(id).occupantKey === null && " · empty vessel"}
              </Chip>
            ))}
          </ControlGroup>
          <ControlGroup label="Ambient (workspace tint)">
            {AMBIENTS.map((a) => (
              <Chip
                key={a.key}
                active={ambient === a.key}
                onClick={() => setAmbient(a.key)}
                testid={`pl-ambient-${a.key}`}
              >
                {a.label}
              </Chip>
            ))}
          </ControlGroup>
        </section>

        {/* Grid: rows = sizes · columns = presence states */}
        <section className="space-y-14" data-testid="pl-grid">
          {SIZES.map(({ label, size }) => (
            <div key={size}>
              <div className="mb-4 flex items-baseline justify-between">
                <h2 className="text-sm font-medium text-stone-200">{label}</h2>
                <span className="text-[10px] uppercase tracking-wider text-stone-500">
                  {size}px
                </span>
              </div>
              <div
                className="grid grid-cols-4 gap-6 rounded-2xl bg-stone-900/60 p-8 border border-stone-800"
                data-testid={`pl-row-${size}`}
              >
                {STATES.map((state) => (
                  <div
                    key={state}
                    className="flex flex-col items-center gap-3"
                    data-testid={`pl-cell-${size}-${state}`}
                  >
                    <div className="flex-1 flex items-center justify-center min-h-[280px]">
                      <CompanionPresence
                        size={size}
                        presence={state}
                        ambient={ambient}
                        profile={activeProfile}
                      />
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500">
                      {state}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Reference — the vessel by itself, no occupant */}
        <section className="mt-16 mb-10">
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="text-sm font-medium text-stone-200">Empty vessel · reference</h2>
            <span className="text-[10px] uppercase tracking-wider text-stone-500">
              The vessel itself
            </span>
          </div>
          <div className="rounded-2xl bg-stone-900/60 p-8 border border-stone-800 flex items-center justify-around">
            {[56, 120, 256].map((s) => (
              <div key={s} className="flex flex-col items-center gap-3">
                <PresenceVessel size={s} presence="present" ambient={ambient} />
                <div className="text-[10px] uppercase tracking-wider text-stone-500">{s}px</div>
              </div>
            ))}
          </div>
        </section>

        <footer className="mt-16 text-xs text-stone-500 max-w-2xl leading-relaxed">
          <strong className="text-stone-300">Design notes.</strong>{" "}
          Ambient tint reflects the active <em>boutique</em>, not the active companion —
          a workspace signal, not a brand. In <code>dormant</code>, the vessel simply
          exists; the occupant is not merely dimmed but genuinely absent. In{" "}
          <code>quiet</code>, the occupant is faintly visible through a slightly heavier
          frost. In <code>present</code>, the occupant gently approaches the interior
          surface — never rising from below, never trapped inside.
        </footer>
      </div>
    </div>
  );
}

function ControlGroup({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs transition-colors ${
        active
          ? "bg-stone-100 text-stone-900"
          : "bg-stone-800 text-stone-300 hover:bg-stone-700"
      }`}
    >
      {children}
    </button>
  );
}
